import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./PriceReviewAdmin.css";

const API_BASE = "https://europe-west1-avisamesibaja-be02c.cloudfunctions.net";
const STORAGE_KEY = "price_review_admin_password";

function formatMoney(amount, currency = "EUR") {
  if (typeof amount !== "number" || Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency,
      minimumFractionDigits: currency === "JPY" ? 0 : 2,
      maximumFractionDigits: currency === "JPY" ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function toInputValue(value) {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

const PAUSE_REASON_LABELS = {
  download_error: "Error de descarga",
  unavailable: "No disponible",
  unknown: "Desconocido",
};

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function PriceReviewAdmin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [draftPassword, setDraftPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [pageMessage, setPageMessage] = useState("");
  const [rowBusy, setRowBusy] = useState({});
  const [pausedItems, setPausedItems] = useState([]);
  const [pausedLoading, setPausedLoading] = useState(false);
  const [pausedMessage, setPausedMessage] = useState("");
  const [pausedBusy, setPausedBusy] = useState({});
  const [reactivatedItems, setReactivatedItems] = useState([]);
  const [reactivatedLoading, setReactivatedLoading] = useState(false);

  const hasPassword = useMemo(() => password.trim().length > 0, [password]);

  const buildHeaders = useCallback(() => ({
    "Content-Type": "application/json",
    "X-Admin-Key": password,
  }), [password]);

  const loadItems = useCallback(async () => {
    if (!hasPassword) return;
    setLoading(true);
    setAuthError("");
    setPageMessage("");
    try {
      const res = await fetch(`${API_BASE}/adminPriceReviews?status=pending`, {
        method: "GET",
        headers: { "X-Admin-Key": password },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "No se pudo cargar");
      }

      setItems(data.items || []);
      setDrafts(Object.fromEntries((data.items || []).map((item) => [
        item.id,
        {
          correctedPrice: toInputValue(
            item.correctedPrice ??
            item.proposedLastNotifiedPrice ??
            item.detectedPrice
          ),
          reviewNotes: item.reviewNotes || "",
        },
      ])));
    } catch (error) {
      setItems([]);
      setAuthError(error.message || "Error cargando revisiones");
    } finally {
      setLoading(false);
    }
  }, [hasPassword, password]);

  const loadPausedItems = useCallback(async () => {
    if (!hasPassword) return;
    setPausedLoading(true);
    setPausedMessage("");
    try {
      const res = await fetch(`${API_BASE}/getAutoPausedProducts`, {
        headers: { "X-Admin-Key": password },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "No se pudo cargar");
      setPausedItems(data.items || []);
    } catch (error) {
      setPausedMessage(error.message || "Error cargando auto-pausados");
    } finally {
      setPausedLoading(false);
    }
  }, [hasPassword, password]);

  const loadReactivatedItems = useCallback(async () => {
    if (!hasPassword) return;
    setReactivatedLoading(true);
    try {
      const res = await fetch(`${API_BASE}/getRecentlyReactivated`, {
        headers: { "X-Admin-Key": password },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "No se pudo cargar");
      setReactivatedItems(data.items || []);
    } catch {
      setReactivatedItems([]);
    } finally {
      setReactivatedLoading(false);
    }
  }, [hasPassword, password]);

  const handleReactivate = async (productId) => {
    setPausedBusy((prev) => ({ ...prev, [productId]: true }));
    setPausedMessage("");
    try {
      const res = await fetch(`${API_BASE}/resetAutoPausedProduct`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Key": password },
        body: JSON.stringify({ productId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "No se pudo reactivar");
      setPausedItems((prev) => prev.filter((p) => p.productId !== productId));
      setPausedMessage(`Reactivado para ${data.resetCount} usuario(s).`);
      loadReactivatedItems();
    } catch (error) {
      setPausedMessage(error.message || "Error reactivando");
    } finally {
      setPausedBusy((prev) => ({ ...prev, [productId]: false }));
    }
  };

  useEffect(() => {
    if (hasPassword) {
      loadItems();
      loadPausedItems();
      loadReactivatedItems();
    }
  }, [hasPassword, loadItems, loadPausedItems, loadReactivatedItems]);

  const handleLogin = async (e) => {
    e.preventDefault();
    sessionStorage.setItem(STORAGE_KEY, draftPassword);
    setPassword(draftPassword);
  };

  const clearAccess = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setDraftPassword("");
    setItems([]);
    setDrafts({});
    setAuthError("");
    setPageMessage("");
  };

  const setDraftField = (id, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const runAction = async (item, action) => {
    const draft = drafts[item.id] || {};
    const correctedPrice = draft.correctedPrice?.trim?.() || "";

    if (action !== "reject" && action !== "mark_unavailable" && correctedPrice === "") {
      setPageMessage("Necesitas poner un precio correcto antes de guardar o aceptar.");
      return;
    }

    setRowBusy((prev) => ({ ...prev, [item.id]: action }));
    setPageMessage("");
    setAuthError("");
    try {
      const res = await fetch(`${API_BASE}/adminPriceReviewAction`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({
          reviewId: item.id,
          action,
          correctedPrice: correctedPrice === "" ? null : Number(correctedPrice.replace(",", ".")),
          reviewNotes: draft.reviewNotes || "",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "No se pudo ejecutar la acción");
      }

      if (action === "send" || action === "reject" || action === "mark_unavailable") {
        setItems((prev) => prev.filter((row) => row.id !== item.id));
      } else {
        setItems((prev) => prev.map((row) => (
          row.id === item.id
            ? { ...row, correctedPrice: Number(correctedPrice.replace(",", ".")), reviewNotes: draft.reviewNotes || "" }
            : row
        )));
      }

      setPageMessage(
        action === "save"
          ? "Precio guardado."
          : action === "send"
            ? (item.reviewType === "global_price_review"
              ? `Precio aceptado. Avisos enviados: ${data.notificationsSent || 0}.`
              : item.reviewType === "last_notified_repair"
                ? `Last notified reparado. Avisos enviados: ${data.notificationsSent || 0}.`
                : `Precio aceptado. Avisos enviados: ${data.notificationsSent || 0}.`)
            : action === "mark_unavailable"
              ? "Marcado como no disponible."
              : "Caso descartado."
      );
    } catch (error) {
      setPageMessage(error.message || "Error ejecutando la acción");
    } finally {
      setRowBusy((prev) => ({ ...prev, [item.id]: "" }));
    }
  };

  if (!hasPassword) {
    return (
      <div className="review-admin-shell">
        <div className="review-admin-login">
          <h1>Revisión de precios</h1>
          <p>Acceso privado para revisar alertas retenidas y correcciones.</p>
          <form onSubmit={handleLogin} className="review-admin-login-form">
            <input
              type="password"
              value={draftPassword}
              onChange={(e) => setDraftPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
            />
            <button type="submit">Entrar</button>
          </form>
          {authError ? <p className="review-admin-error">{authError}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="review-admin-shell">
      <div className="review-admin-header">
        <div>
          <h1>Panel de administración</h1>
        </div>
        <div className="review-admin-header-actions">
          <button type="button" onClick={() => { loadItems(); loadPausedItems(); loadReactivatedItems(); }} disabled={loading || pausedLoading}>
            {loading || pausedLoading ? "Actualizando..." : "Actualizar"}
          </button>
          <button type="button" className="secondary" onClick={clearAccess}>
            Salir
          </button>
        </div>
      </div>

      {pageMessage ? <div className="review-admin-message">{pageMessage}</div> : null}
      {authError ? <div className="review-admin-error">{authError}</div> : null}

      <div className="review-admin-section-header">
        <h2>Revisiones de precio</h2>
        <span className="review-admin-count">{items.length} pendientes</span>
      </div>

      <div className="review-admin-table-wrap">
        <table className="review-admin-table">
          <thead>
            <tr>
              <th>Producto</th>
              <th>Detectado</th>
              <th>Refs</th>
              <th>Correcto</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const draft = drafts[item.id] || {};
              const busy = rowBusy[item.id];
              const isGlobalReview = item.reviewType === "global_price_review";
              const isLastNotifiedRepair = item.reviewType === "last_notified_repair";
              return (
                <tr key={item.id}>
                  <td>
                    <div className="review-admin-product">
                      <strong>{item.title}</strong>
                      <span>{item.email || item.productId}</span>
                      <a href={item.productUrl} target="_blank" rel="noreferrer">Ver en Amazon</a>
                    </div>
                  </td>
                  <td>
                    <div className="review-admin-price-main">
                      {formatMoney(item.detectedPrice, item.currency)}
                    </div>
                    <div className="review-admin-meta">
                      {isGlobalReview
                        ? "revision global"
                        : isLastNotifiedRepair
                          ? "reparar last notified"
                          : "retenido"}
                    </div>
                  </td>
                  <td>
                    {!isGlobalReview && !isLastNotifiedRepair ? (
                      <div className="review-admin-meta">Inicial: {formatMoney(item.initialPrice, item.currency)}</div>
                    ) : null}
                    {!isLastNotifiedRepair ? (
                      <div className="review-admin-meta">Prev global: {formatMoney(item.previousGlobalPrice, item.currency)}</div>
                    ) : null}
                    {!isGlobalReview && !isLastNotifiedRepair ? (
                      <div className="review-admin-meta">Ult notif: {formatMoney(item.lastNotifiedPrice ?? item.badNotifiedPrice, item.currency)}</div>
                    ) : null}
                    {isLastNotifiedRepair ? (
                      <>
                        <div className="review-admin-meta">Actual producto: {formatMoney(item.currentPrice ?? item.detectedPrice, item.currency)}</div>
                        <div className="review-admin-meta">Ult notif mala: {formatMoney(item.badNotifiedPrice ?? item.lastNotifiedPrice, item.currency)}</div>
                        <div className="review-admin-meta">Propuesta: {formatMoney(item.proposedLastNotifiedPrice, item.currency)}</div>
                        <div className="review-admin-meta">Notif. ahora: {item.shouldNotifyNow ? "si" : "no"}</div>
                      </>
                    ) : null}
                  </td>
                  <td>
                    <input
                      className="review-admin-price-input"
                      type="number"
                      step="0.01"
                      value={draft.correctedPrice || ""}
                      onChange={(e) => setDraftField(item.id, "correctedPrice", e.target.value)}
                      placeholder={isLastNotifiedRepair ? "Last notified correcto" : "Precio correcto"}
                    />
                  </td>
                  <td>
                    <div className="review-admin-actions">
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => runAction(item, "send")}
                      >
                        {busy === "send" ? "Guardando..." : "Guardar"}
                      </button>
                      {isGlobalReview || isLastNotifiedRepair ? (
                        <button
                          type="button"
                          className="warning"
                          disabled={!!busy}
                          onClick={() => runAction(item, "mark_unavailable")}
                        >
                          {busy === "mark_unavailable" ? "Marcando..." : "No disponible"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="danger"
                        disabled={!!busy}
                        onClick={() => runAction(item, "reject")}
                      >
                        {busy === "reject" ? "Descartando..." : "Descartar"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan="5" className="review-admin-empty">
                  No hay casos pendientes.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="review-admin-section-header" style={{ marginTop: "40px" }}>
        <h2>Reactivados recientemente</h2>
        <span className="review-admin-count">últimos 7 días</span>
      </div>

      <div className="review-admin-table-wrap">
        <table className="review-admin-table review-admin-reactivated-table">
          <thead>
            <tr>
              <th>Producto</th>
              <th>Reactivado</th>
              <th>Precio actual</th>
              <th>Última verificación</th>
            </tr>
          </thead>
          <tbody>
            {reactivatedItems.map((item) => (
              <tr key={item.productId}>
                <td>
                  <div className="review-admin-product">
                    <strong>{item.title || item.productId}</strong>
                    <span>{item.productId}</span>
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer">Ver en Amazon</a>}
                  </div>
                </td>
                <td>
                  <div className="review-admin-meta">{formatDate(item.reactivatedAt)}</div>
                </td>
                <td>
                  {item.currentPrice != null
                    ? <strong>{formatMoney(item.currentPrice, item.currency)}</strong>
                    : <span className="review-admin-meta">Sin precio</span>}
                </td>
                <td>
                  <div className="review-admin-meta">{formatDate(item.lastVerifiedAt)}</div>
                </td>
              </tr>
            ))}
            {!reactivatedLoading && reactivatedItems.length === 0 ? (
              <tr>
                <td colSpan="4" className="review-admin-empty">
                  No hay reactivaciones en los últimos 7 días.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="review-admin-section-header" style={{ marginTop: "40px" }}>
        <h2>Alertas auto-pausadas</h2>
        <span className="review-admin-count">{pausedItems.length} productos</span>
      </div>

      {pausedMessage ? <div className="review-admin-message review-admin-paused-message">{pausedMessage}</div> : null}

      <div className="review-admin-table-wrap">
        <table className="review-admin-table review-admin-paused-table">
          <thead>
            <tr>
              <th>Producto</th>
              <th>Razón</th>
              <th>Pausado</th>
              <th>Usuarios afectados</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pausedItems.map((item) => (
              <tr key={item.productId}>
                <td>
                  <div className="review-admin-product">
                    <strong>{item.title || item.productId}</strong>
                    <span>{item.productId}</span>
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer">Ver en Amazon</a>}
                    {item.currentPrice != null && (
                      <span>Precio actual: {formatMoney(item.currentPrice, item.currency)}</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`review-admin-reason review-admin-reason-${item.reason}`}>
                    {PAUSE_REASON_LABELS[item.reason] || item.reason}
                  </span>
                </td>
                <td>
                  <div className="review-admin-meta">{formatDate(item.autoPausedAt)}</div>
                  {item.monitoringFailureSinceAt && (
                    <div className="review-admin-meta">Fallo desde: {formatDate(item.monitoringFailureSinceAt)}</div>
                  )}
                </td>
                <td>
                  {item.affectedUsers.map((u) => (
                    <div key={u.userId} className="review-admin-meta">{u.email || u.userId}</div>
                  ))}
                </td>
                <td>
                  <div className="review-admin-actions">
                    <button
                      type="button"
                      disabled={!!pausedBusy[item.productId]}
                      onClick={() => handleReactivate(item.productId)}
                    >
                      {pausedBusy[item.productId] ? "Reactivando..." : "Reactivar"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!pausedLoading && pausedItems.length === 0 ? (
              <tr>
                <td colSpan="5" className="review-admin-empty">
                  No hay alertas auto-pausadas.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
